/** The readable cookie, on the environment's parent domain, that holds the player's choice for every app. */
export const THEME_COOKIE = 'wh-theme';

/** A year, refreshed whenever the player chooses again. */
export const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const THEME_PREFERENCES = ['light', 'dark', 'auto'] as const;

/** What the player chose: Meadow, Noir, or whichever of the two matches the device's light or dark setting. */
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** What a missing, empty or unrecognised cookie reads as. */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

/** The theme a preference puts on screen. */
export type PreferredTheme = 'meadow' | 'noir';

/** The media query `auto` follows. */
export const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

export const isThemePreference = (value: unknown): value is ThemePreference =>
  THEME_PREFERENCES.includes(value as ThemePreference);

/** `value` when it is a preference, otherwise `light`. */
export const parseThemePreference = (value: string | null | undefined): ThemePreference =>
  isThemePreference(value) ? value : DEFAULT_THEME_PREFERENCE;

/** The preference in a `document.cookie` string: the first `wh-theme` cookie, or `light` when there is none. */
export const readThemePreference = (cookies: string): ThemePreference => {
  const prefix = `${THEME_COOKIE}=`;
  const cookie = cookies
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(prefix));

  return parseThemePreference(cookie?.slice(prefix.length));
};

/** The `document.cookie` assignment that saves `preference` on `domain` for a year. */
export const themePreferenceCookie = (preference: ThemePreference, domain: string): string => {
  if (!isThemePreference(preference)) {
    throw new RangeError(
      `${JSON.stringify(preference)} is not a theme preference: use ${THEME_PREFERENCES.join(', ')}`,
    );
  }
  if (!domain.trim()) {
    throw new RangeError('The theme cookie needs the session cookie domain');
  }

  return `${THEME_COOKIE}=${preference}; Max-Age=${THEME_COOKIE_MAX_AGE}; Domain=${domain}; Path=/; Secure; SameSite=Lax`;
};

/** The theme `preference` shows on a device that does, or does not, prefer a dark colour scheme. */
export const resolveThemePreference = (preference: ThemePreference, prefersDark: boolean): PreferredTheme => {
  switch (preference) {
    case 'dark':
      return 'noir';
    case 'auto':
      return prefersDark ? 'noir' : 'meadow';
    default:
      return 'meadow';
  }
};
